import React from 'react'

import {
  Button,
  CurrencyInput,
  Fieldset,
  ModalBox,
  PriceString,
  RadioButton,
  useModalContext,
  useToastsContext,
} from '@fourthwall/components'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'

import type { useMemberQuery } from '../../hooks/useMemberQuery'
import { formatCurrencyFromDecimal } from '../../utils'
import { SubscriptionCancelModalSkeleton } from './SubscriptionCancelModalSkeleton'
import { useSubscriptionCancelModal } from './hooks/useSubscriptionCancelModal'
import * as S from './styled'
import { CancelRequestType, FormFields, RefundRequestType } from './types'

type Props = {
  member: NonNullable<ReturnType<typeof useMemberQuery>['memberQuery']['data']>
  onSuccess: () => void
}

export const SubscriptionCancelModal = ({ member, onSuccess }: Props) => {
  const { form, refundAmount, lastCharge, isLoading, isFetching } = useSubscriptionCancelModal({
    member,
    onSuccess: (cancelRequestType, refundAmount) => {
      let copy = ''

      if (cancelRequestType === 'Now' && refundAmount) {
        copy =
          'Refund issued and subscription cancelled. Please allow up to a minute for changes to show on this page.'
      }

      if (cancelRequestType === 'Schedule' && refundAmount) {
        copy =
          'Refund issued and subscription scheduled to cancel. Please allow up to a minute for changes to show on this page.'
      }

      if (cancelRequestType === 'Now' && !refundAmount) {
        copy =
          'Subscription cancelled. Please allow up to a minute for changes to show on this page.'
      }

      if (cancelRequestType === 'Schedule' && !refundAmount) {
        copy =
          'Subscription scheduled to cancel. Please allow up to a minute for changes to show on this page.'
      }

      toast.notify(copy, { type: 'info' })
      modal.close()
      onSuccess()
    },
  })
  const modal = useModalContext()
  const toast = useToastsContext()

  if (isFetching) return <SubscriptionCancelModalSkeleton member={member} />

  return (
    <ModalBox
      title={
        <>
          Cancel {member.nickname}'s {member.tier?.name} subscription
        </>
      }
      size="medium"
      buttons={[
        <Button label="Close" appearance="secondary" onClick={modal.close} />,
        <Button
          label={
            refundAmount && lastCharge ? (
              <>
                Confirm cancellation and refund{' '}
                <PriceString value={refundAmount} currency={lastCharge.currency.code} />
              </>
            ) : (
              'Confirm cancellation'
            )
          }
          appearance="destructive"
          loading={isLoading}
          onClick={form.submitForm}
        />,
      ]}
      onClose={modal.close}
    >
      <S.Container>
        {member.cancelRequest.type === 'Allowed' && (
          <Fieldset>
            <Fieldset.Row>
              <S.Bold>When do you want to cancel this subscription?</S.Bold>
            </Fieldset.Row>
            <Fieldset.Row>
              <RadioButton
                label="Remove access immediately"
                checkedValue={CancelRequestType.Now}
                disabled={isLoading}
                {...form.getFieldProps(FormFields.cancelRequestType)}
              />
            </Fieldset.Row>
            <Fieldset.Row>
              <RadioButton
                label={
                  <>
                    Remove access at end of cycle &nbsp;
                    <TimezonedDateString
                      date={member.cancelRequest.subscriptionCurrentPeriodEnd}
                      format="(MMM D, YYYY)"
                    />
                  </>
                }
                checkedValue={CancelRequestType.Schedule}
                disabled={isLoading}
                {...form.getFieldProps(FormFields.cancelRequestType)}
              />
            </Fieldset.Row>
          </Fieldset>
        )}
        {lastCharge && (
          <Fieldset>
            <Fieldset.Row>
              <S.Bold>Do you want to issue a refund for this subscription?</S.Bold>
            </Fieldset.Row>
            {lastCharge.refundRequest.type === 'Allowed' && (
              <Fieldset.Row>
                <RadioButton
                  label={
                    <>
                      Full refund (
                      <PriceString
                        value={formatCurrencyFromDecimal(
                          lastCharge.refundRequest.options.full.amount,
                          lastCharge.currency.decimalPlaces,
                        )}
                        currency={lastCharge.currency.code}
                      />
                      )
                    </>
                  }
                  checkedValue={RefundRequestType.full}
                  disabled={isLoading}
                  {...form.getFieldProps(FormFields.refundRequestType)}
                />
              </Fieldset.Row>
            )}
            {lastCharge.refundRequest.type === 'Allowed' &&
              lastCharge.refundRequest.options.prorated.type !== 'NotAllowed' && (
                <Fieldset.Row>
                  <RadioButton
                    label={
                      <>
                        Pro-rated refund (
                        <PriceString
                          value={formatCurrencyFromDecimal(
                            lastCharge.refundRequest.options.prorated.amount,
                            lastCharge.currency.decimalPlaces,
                          )}
                          currency={lastCharge.currency.code}
                        />
                        , based on {lastCharge.refundRequest.options.prorated.daysLeft} out of{' '}
                        {lastCharge.refundRequest.options.prorated.daysTotal} days unused)
                      </>
                    }
                    checkedValue={RefundRequestType.prorated}
                    disabled={
                      lastCharge.refundRequest.options.prorated.type === 'Disabled' || isLoading
                    }
                    {...form.getFieldProps(FormFields.refundRequestType)}
                  />
                </Fieldset.Row>
              )}
            {lastCharge.refundRequest.type === 'Allowed' && (
              <>
                <Fieldset.Row>
                  <RadioButton
                    label="Custom amount"
                    checkedValue={RefundRequestType.custom}
                    disabled={isLoading}
                    {...form.getFieldProps(FormFields.refundRequestType)}
                  />
                </Fieldset.Row>
                <Fieldset.Row>
                  <RadioButton
                    label="No refund"
                    checkedValue={RefundRequestType.none}
                    disabled={isLoading}
                    {...form.getFieldProps(FormFields.refundRequestType)}
                  />
                </Fieldset.Row>
              </>
            )}
            {lastCharge.refundRequest.type === 'Disabled' && (
              <Fieldset.Row>
                {
                  {
                    AlreadyRefunded:
                      'Refund is not available. Last transaction for this member has been already refunded.',
                    TooSmallAmount:
                      'Refund is not available. The amount is smaller than the minimum refund amount ($1).',
                    IapCharge:
                      'Refund is not available. The subscription is managed by the mobile app operator (Android/iOS). Member has to request a refund from the in-app operator.',
                    TooOldPayPalCharge:
                      'Refund is not available. Due to PayPal policy, payments older than 6 months cannot be refunded.',
                  }[lastCharge.refundRequest.reason]
                }
              </Fieldset.Row>
            )}
            {form.values.refundRequestType === RefundRequestType.custom && (
              <Fieldset.Row inner>
                <CurrencyInput
                  label="Custom amount"
                  disabled={isLoading}
                  {...form.getFieldProps(FormFields.customAmount)}
                />
              </Fieldset.Row>
            )}
          </Fieldset>
        )}
        {!lastCharge && (
          <Fieldset>
            <Fieldset.Row>
              <S.Bold>Do you want to issue a refund for this subscription?</S.Bold>
            </Fieldset.Row>
            <Fieldset.Row>There are no charges to refund.</Fieldset.Row>
          </Fieldset>
        )}
        {lastCharge?.refundRequest.type === 'Allowed' &&
          form.values.refundRequestType !== RefundRequestType.none && (
            <p>
              Refunds cannot be undone. The refund amount{' '}
              {refundAmount ? (
                <>
                  of <PriceString value={refundAmount} currency={lastCharge.currency.code} />{' '}
                </>
              ) : undefined}
              will be deducted from your account balance. Refunds will be returned within 48 hours
              to the original payment method used.
            </p>
          )}
      </S.Container>
    </ModalBox>
  )
}
